/**
 * Turnkey Town-Site CMS (ADR-0020, docs/features/turnkey-site.md §1/§2) — zod
 * schemas + inferred types for Page/PageSection/Official/SiteNav/SiteTheme
 * and the staff input DTOs, mirroring the Go structs in
 * services/api/internal/shared/schemas.go field-for-field (same JSON tags,
 * same bounds from validate.go) so apps/web (react-hook-form + zod resolver,
 * Pinky's P1 build) can import a single source of truth instead of
 * hand-rolling parallel TS interfaces.
 *
 * ISOMORPHIC: browser-safe (no node:* / aws-sdk), matching schemas.ts's own
 * constraint (consumed by @td/widget too, in principle).
 *
 * P1 SCOPE NOTE: these schemas describe the STORED/API shapes as they exist
 * today. The Astro-build/rebuild trigger (ADR-0020 Open Decision 1) is not
 * ratified — nothing here encodes a build-status field; the console reports
 * plain create/update/publish/unpublish results only (see admin_pages.go's
 * own // TODO(P2) seam).
 */

import { z } from "zod";

// ---- Field limits (mirrors shared/schemas.go's Max* constants exactly) -----

export const MAX_PAGE_TITLE_LEN = 200;
export const MAX_PAGE_PERMALINK_LEN = 200;
export const MAX_PAGE_DESCRIPTION_LEN = 300;
/** MaxSectionsPerPage — validate.go/schemas.go. */
export const MAX_SECTIONS_PER_PAGE = 60;
/** MaxFAQAnswerHTMLLen is reused as the section bodyHtml bound (validate.go's ValidatePageSectionInput). */
export const MAX_SECTION_BODY_HTML_LEN = 20000;
export const MAX_OFFICIAL_NAME_LEN = 200;
export const MAX_OFFICIAL_ROLE_LEN = 200;
export const MAX_SITE_NAV_ITEMS = 30;
export const MAX_SITE_NAV_CHILDREN = 15;
export const MAX_SITE_NAV_LABEL_LEN = 100;
/** MaxNameLen reused as SiteTheme.FontFamily's bound (validate.go's ValidatePutSiteTheme). */
export const MAX_SITE_THEME_FONT_FAMILY_LEN = 200;

// ---- PageSection structured block-data limits (mirrors schemas.go's Max*
// constants added alongside PageSectionData exactly) -------------------------

export const MAX_CARDS_PER_SECTION = 24;
export const MAX_DOCUMENTS_PER_SECTION = 40;
export const MAX_CARD_TITLE_LEN = 200;
/** MaxFAQAnswerHTMLLen is reused as CardItem.Blurb's bound (validate.go's validateCardItem). */
export const MAX_CARD_BLURB_LEN = 20000;
export const MAX_CARD_ICON_LEN = 100;
export const MAX_DOCUMENT_TITLE_LEN = 200;
export const MAX_DOCUMENT_TYPE_LEN = 32;
export const MAX_EMBED_ID_LEN = 200;
/** MaxAttachmentKeyLen is reused as DocumentItem.FileKey's bound (validate.go's validateDocumentItem). */
export const MAX_DOCUMENT_FILE_KEY_LEN = 1024;
/** MaxDocBytes — the ceiling DocumentItem.Size (bytes, display-only/client-supplied) is checked against. */
export const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024;
/** MaxRecordsDateLen is reused as DocumentItem.Date's bound. */
export const MAX_DOCUMENT_DATE_LEN = 32;

// ---- Enums (mirror the Go const/map "enum" pattern) -------------------------

/** PageStatus — shared/schemas.go. A third value never appears (see the Go doc comment). */
export const PAGE_STATUSES = ["draft", "published"] as const;
export const pageStatusSchema = z.enum(PAGE_STATUSES);
export type PageStatus = z.infer<typeof pageStatusSchema>;

/**
 * PageSectionKind — shared/schemas.go. `accordion` is EXPLICITLY DEFERRED
 * (ADR-0020) — do not add it here without a corresponding Go-side change.
 */
export const PAGE_SECTION_KINDS = ["richtext", "callout", "embed", "cards", "documents"] as const;
export const pageSectionKindSchema = z.enum(PAGE_SECTION_KINDS);
export type PageSectionKind = z.infer<typeof pageSectionKindSchema>;

/** OfficialGroup — shared/schemas.go. */
export const OFFICIAL_GROUPS = ["elected", "staff"] as const;
export const officialGroupSchema = z.enum(OFFICIAL_GROUPS);
export type OfficialGroup = z.infer<typeof officialGroupSchema>;

/**
 * SiteEmbedModule — shared/schemas.go. The well-known catalog key an "embed"
 * block may reference, mirroring GET /api/admin/site-embeds's own entries
 * (handler.AdminSiteEmbeds) — never a free-text embed src.
 */
export const SITE_EMBED_MODULES = [
  "concern",
  "ask-faq",
  "announcements",
  "meetings",
  "ordinance",
  "subscribe",
] as const;
export const siteEmbedModuleSchema = z.enum(SITE_EMBED_MODULES);
export type SiteEmbedModule = z.infer<typeof siteEmbedModuleSchema>;

/**
 * SITE_EMBED_MODULE_ROUTES — mirrors shared.SiteEmbedModuleRoutes exactly
 * (the resident-facing standalone route each module resolves to). The
 * server (GET /api/public/site-content) already resolves a `tool` nav entry
 * to its real href — this constant exists so a frontend client rendering a
 * LIVE PREVIEW of an unsaved nav/embed edit (before the next save round-trips
 * through the server) can show the same route without a network call.
 */
export const SITE_EMBED_MODULE_ROUTES: Record<SiteEmbedModule, string> = {
  concern: "/report",
  "ask-faq": "/ask",
  announcements: "/announcements",
  meetings: "/meetings",
  ordinance: "/code",
  subscribe: "/notify/subscribe",
};

// ---- Permalink format guard (mirrors validate.go's rePagePermalink) --------

/**
 * Lowercase letters/digits/hyphens only, no leading/trailing/doubled hyphen
 * (validate.go's rePagePermalink doc comment — uniqueness is a store-layer/
 * handler-layer concern, NOT expressible here).
 */
export const PAGE_PERMALINK_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const pagePermalinkSchema = z
  .string()
  .trim()
  .min(1, "Enter a permalink")
  .max(MAX_PAGE_PERMALINK_LEN, "Permalink is too long")
  .regex(
    PAGE_PERMALINK_PATTERN,
    "Permalink must be lowercase letters, digits, and hyphens only (no leading/trailing/double hyphen)",
  );

// ---- PageSection structured block data (mirrors shared.PageSectionData /
// CardItem / DocumentItem / PageSectionEmbed field-for-field — W2
// backend-contract-completion) --------------------------------------------
//
// richtext/callout sections keep using PageSection.bodyHtml (unchanged);
// embed/cards/documents sections carry their payload in `data` instead, as a
// PER-KIND shape (exactly one of data.embed/data.cards/data.documents is set,
// matching the section's own `kind` — see the Go PageSectionData doc comment
// for the full rationale). Any HTML inside `data` (CardItem.blurb) is
// sanitized server-side on write, same as bodyHtml.

/**
 * PageSectionEmbed — a reference to an embeddable TownDesk module (the
 * `embed` block's payload). `module` is the closed catalog key
 * (SITE_EMBED_MODULES) — never a free-text embed src.
 */
export const pageSectionEmbedSchema = z
  .object({
    module: siteEmbedModuleSchema,
    embedId: z.string().max(MAX_EMBED_ID_LEN, "embedId is too long").optional(),
  })
  .strict();

export type PageSectionEmbed = z.infer<typeof pageSectionEmbedSchema>;

/** CardItem — one card in a "cards" block. */
export const cardItemSchema = z
  .object({
    icon: z.string().max(MAX_CARD_ICON_LEN, "Icon is too long").optional(),
    title: z.string().trim().min(1, "Enter a title").max(MAX_CARD_TITLE_LEN, "Title is too long"),
    /** Server-sanitized on write — see the module doc comment above. */
    blurb: z.string().max(MAX_CARD_BLURB_LEN, "Blurb is too long").optional(),
    href: z.string().optional(),
    /** Uploaded-path/URL reference (POST /api/admin/site-files/upload) — the reference only, never image bytes. */
    photo: z.string().optional(),
  })
  .strict();

export type CardItem = z.infer<typeof cardItemSchema>;

/** DocumentItem — one file in a "documents" block. */
export const documentItemSchema = z
  .object({
    title: z.string().trim().min(1, "Enter a title").max(MAX_DOCUMENT_TITLE_LEN, "Title is too long"),
    /** Uploaded-path/URL reference (POST /api/admin/site-files/upload). */
    fileKey: z.string().min(1, "Missing fileKey").max(MAX_DOCUMENT_FILE_KEY_LEN),
    type: z.string().max(MAX_DOCUMENT_TYPE_LEN).optional(),
    /** Bytes — display-only, client-supplied at upload time (not independently re-verified server-side). */
    size: z.number().int().min(0).max(MAX_DOCUMENT_SIZE_BYTES).optional(),
    /** ISO-8601 date string (e.g. an ordinance PDF's "adopted"/"revised" date) — distinct from any audit timestamp. */
    date: z.string().max(MAX_DOCUMENT_DATE_LEN).optional(),
  })
  .strict();

export type DocumentItem = z.infer<typeof documentItemSchema>;

/**
 * PageSectionData — the discriminated per-kind block payload. Every
 * sub-field is optional at the SCHEMA level (a plain `.strict()` object, not
 * a `z.discriminatedUnion`, since the Go wire shape carries `data` as one
 * struct with omitempty fields rather than a tagged union) — cross-field
 * "exactly the field matching this section's kind is set" enforcement lives
 * in pageSectionInputSchema's `.refine()` below (mirrors
 * ValidatePageSectionData's Go-side enforcement), not here, so this schema
 * alone can still describe a GET response read back from the API.
 */
export const pageSectionDataSchema = z
  .object({
    embed: pageSectionEmbedSchema.optional(),
    cards: z.array(cardItemSchema).max(MAX_CARDS_PER_SECTION).optional(),
    documents: z.array(documentItemSchema).max(MAX_DOCUMENTS_PER_SECTION).optional(),
  })
  .strict();

export type PageSectionData = z.infer<typeof pageSectionDataSchema>;

// ---- Entities (persisted/API-read shapes) ------------------------------------

/**
 * PageSection — one ordered content block within a Page. BodyHTML is
 * server-sanitized on write (shared.SanitizeAnswerHTML) and never
 * re-sanitized on read — this schema describes the value AFTER that
 * server-side transform, same as reading it back from the API. `data` is
 * used by embed/cards/documents kinds (see PageSectionData's doc comment
 * above); absent for richtext/callout.
 */
export const pageSectionSchema = z
  .object({
    tenantId: z.string(),
    pageId: z.string(),
    sectionId: z.string(),
    order: z.number(),
    kind: pageSectionKindSchema,
    bodyHtml: z.string().optional(),
    data: pageSectionDataSchema.optional(),
  })
  .strict();

export type PageSection = z.infer<typeof pageSectionSchema>;

/**
 * Page — a generic editor page (About/History/a department page/…), the
 * evergreen content the Astro shell sources at (rare) build time.
 */
export const pageSchema = z
  .object({
    tenantId: z.string(),
    pageId: z.string(),
    title: z.string(),
    permalink: z.string(),
    description: z.string().optional(),
    order: z.number(),
    status: pageStatusSchema,
    publishedAt: z.string().optional(),
    updatedBy: z.string().optional(),
    /** Clerk-set "I confirmed this is still accurate" timestamp — an accuracy signal, not a publish gate. */
    lastReviewed: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();

export type Page = z.infer<typeof pageSchema>;

/** PageBundle — store.GetPageBundle's return shape, also AdminPageByID's GET response shape. */
export const pageBundleSchema = z
  .object({
    page: pageSchema,
    sections: z.array(pageSectionSchema),
  })
  .strict();

export type PageBundle = z.infer<typeof pageBundleSchema>;

/** Official — one elected/staff directory row. */
export const officialSchema = z
  .object({
    tenantId: z.string(),
    id: z.string(),
    name: z.string(),
    role: z.string(),
    group: officialGroupSchema,
    order: z.number(),
    email: z.string().optional(),
    phone: z.string().optional(),
    /** S3 key/URL of a presigned-upload photo — this schema stores only the reference. */
    photo: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();

export type Official = z.infer<typeof officialSchema>;

/**
 * SiteNavItem — one entry in TenantSettings.SiteNav. Exactly one of
 * pageId/externalUrl/tool is set (ValidatePutSiteNav's cross-field rule —
 * encoded below via `.refine`, mirroring intakeLocationSchema's own
 * cross-field refine in schemas.ts). `tool` is a THIRD target kind (W2): a
 * reference to a well-known TownDesk module (the SAME closed
 * SITE_EMBED_MODULES catalog the "embed" PageSection block uses), resolved
 * to that module's real route by the server (GET /api/public/site-content —
 * see shared.SiteEmbedModuleRoutes). Children models ONE level of dropdown
 * nesting; a grandchild is rejected, not silently ignored (see the Go doc
 * comment).
 */
const siteNavItemBase = z.object({
  label: z.string().trim().min(1, "Enter a label").max(MAX_SITE_NAV_LABEL_LEN, "Label is too long"),
  pageId: z.string().optional(),
  externalUrl: z.string().optional(),
  tool: siteEmbedModuleSchema.optional(),
  order: z.number(),
});

export type SiteNavItem = z.infer<typeof siteNavItemBase> & { children?: SiteNavItem[] };

export const siteNavItemSchema: z.ZodType<SiteNavItem> = siteNavItemBase
  .extend({
    children: z.lazy(() => z.array(siteNavItemSchema).max(MAX_SITE_NAV_CHILDREN)).optional(),
  })
  .strict()
  .refine(
    (item) => [Boolean(item.pageId), Boolean(item.externalUrl), Boolean(item.tool)].filter(Boolean).length === 1,
    {
      message: "exactly one of pageId, externalUrl, or tool must be set",
      path: ["pageId"],
    },
  );

/**
 * SiteTheme — the Astro-shell theming whitelist (ADR-0020 §8, ADR-0010
 * theming whitelist). Deliberately narrow: only accent/logo/fonts are
 * town-configurable — there is NO ink/spacing/contrast field to smuggle
 * through even by including it in a submitted object (`.strict()` rejects
 * unknown keys; the Go side ignores them by omission, this schema rejects
 * them outright, which is stricter and still consistent with the whitelist).
 */
export const siteThemeSchema = z
  .object({
    accentColor: z.string().optional(),
    logoUrl: z.string().optional(),
    fontFamily: z.string().max(MAX_SITE_THEME_FONT_FAMILY_LEN).optional(),
  })
  .strict();

export type SiteTheme = z.infer<typeof siteThemeSchema>;

// ---- Staff input DTOs (turnkey-site.md §2, mirrors shared/schemas.go's
// "Staff input DTOs" section field-for-field) ---------------------------------
//
// Every input below deliberately omits tenantId/pageId-or-id/updatedBy/
// timestamps — those are NEVER accepted from the client (server-resolved
// from the authenticated admin's auth context + the route path), same
// discipline as submitReportInputSchema's own doc comment in schemas.ts.

/**
 * PageSectionInput — the staff-submitted shape of ONE section within a
 * PutPageInput.sections list. sectionId is OPTIONAL: omit/empty means "new
 * section, server mints an id"; echo back an existing sectionId to preserve
 * identity across an edit (see the Go PageSectionInput doc comment).
 * bodyHtml is used by richtext/callout kinds; data is used by
 * embed/cards/documents kinds — the `.refine()` below mirrors
 * ValidatePageSectionData's Go-side cross-field enforcement exactly: data
 * must be ABSENT/empty for richtext/callout, and must carry the ONE matching
 * sub-payload (non-empty) for embed/cards/documents.
 */
const pageSectionInputBase = z.object({
  sectionId: z.string().optional(),
  order: z.number(),
  kind: pageSectionKindSchema,
  bodyHtml: z.string().max(MAX_SECTION_BODY_HTML_LEN),
  data: pageSectionDataSchema.optional(),
});

function isDataEmpty(data: PageSectionData | undefined): boolean {
  return !data || (!data.embed && (!data.cards || data.cards.length === 0) && (!data.documents || data.documents.length === 0));
}

export const pageSectionInputSchema = pageSectionInputBase.strict().refine(
  (sec) => {
    switch (sec.kind) {
      case "richtext":
      case "callout":
        return isDataEmpty(sec.data);
      case "embed":
        return Boolean(sec.data?.embed);
      case "cards":
        return Boolean(sec.data?.cards && sec.data.cards.length > 0);
      case "documents":
        return Boolean(sec.data?.documents && sec.data.documents.length > 0);
      default:
        return false;
    }
  },
  {
    message:
      "data must match kind: richtext/callout carry no data; embed/cards/documents each require their own matching, non-empty data payload",
    path: ["data"],
  },
);

export type PageSectionInput = z.infer<typeof pageSectionInputSchema>;

/**
 * PutPageInput — the staff body for POST /api/admin/pages (create) and PUT
 * /api/admin/pages/{pageId} (update). A full-replace upsert of the editable
 * fields + the WHOLE sections list — the server reconciles Sections against
 * whatever is currently stored (add/update/delete-orphan), so the client
 * always submits the complete desired section list, never a diff.
 */
export const putPageInputSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, "Enter a title")
      .max(MAX_PAGE_TITLE_LEN, "Title is too long"),
    permalink: pagePermalinkSchema,
    description: z.string().max(MAX_PAGE_DESCRIPTION_LEN, "Description is too long").optional(),
    order: z.number(),
    status: pageStatusSchema,
    sections: z.array(pageSectionInputSchema).max(MAX_SECTIONS_PER_PAGE),
  })
  .strict();

export type PutPageInput = z.infer<typeof putPageInputSchema>;

/** PutOfficialInput — the staff body for POST /api/admin/officials (create) and PUT /api/admin/officials/{id} (update). */
export const putOfficialInputSchema = z
  .object({
    name: z.string().trim().min(1, "Enter a name").max(MAX_OFFICIAL_NAME_LEN, "Name is too long"),
    role: z.string().trim().min(1, "Enter a role").max(MAX_OFFICIAL_ROLE_LEN, "Role is too long"),
    group: officialGroupSchema,
    order: z.number(),
    email: z.string().email("Enter a valid email").max(320).optional().or(z.literal("")),
    phone: z.string().max(32).optional(),
    photo: z.string().optional(),
  })
  .strict();

export type PutOfficialInput = z.infer<typeof putOfficialInputSchema>;

/** PutSiteNavInput — the staff body for GET/PUT /api/admin/site-nav. A full-replace of TenantSettings.SiteNav. */
export const putSiteNavInputSchema = z
  .object({
    items: z.array(siteNavItemSchema).max(MAX_SITE_NAV_ITEMS),
  })
  .strict();

export type PutSiteNavInput = z.infer<typeof putSiteNavInputSchema>;

/** PutSiteThemeInput — the staff body for GET/PUT /api/admin/site-theme. A full-replace of TenantSettings.SiteTheme. */
export const putSiteThemeInputSchema = siteThemeSchema;

export type PutSiteThemeInput = z.infer<typeof putSiteThemeInputSchema>;
